import { parisDayToEpoch } from "./dates";

// Mapping entre les noms de ressources React-Admin et les chemins backend.
// Pour les ressources sub-resources (ex: /pro/{proId}/exercises), on utilise
// `dynamicPath: "pro/:proId/exercises"` (params pris dans les filtres) et
// `itemPath` pour les opérations unitaires (cf. buildDynamicPath).
// `listFilter` permet d'injecter des filtres par défaut côté backend.

// Comptes supprimés (anonymisés en deleted-…@deleted.local) : masqués par
// défaut des listes d'utilisateurs ; le filtre « showDeleted » (côté BO
// uniquement, jamais envoyé au backend) les réaffiche.
export const isDeletedAccount = (r) => /^deleted-.*@deleted\.local$/i.test(String(r?.email || ""));
export const DELETED_ACCOUNTS_FILTER = { key: "showDeleted", exclude: isDeletedAccount };

export const resourceMap = {
  // Identité
  // `hideTestUsers: true` = on masque par défaut les comptes de tests internes
  // (emails @iouman.com créés par les scripts QA). Le back-office peut réafficher
  // ces comptes en décochant le BooleanInput dédié dans la barre de filtres —
  // params.filter écrase listFilter grâce à l'ordre du spread dans le dataProvider.
  // Lecture : GET /users/{id}. Écriture : PUT /users/pro/{id} ou
  // /users/patient/{id} (il n'existe ni PUT /users/{id} ni POST /users).
  // Filtres GET /users supportés : type, hideTestUsers, specialty, city,
  // consultationType, fromTime/toTime.
  // Ligne sans `type` : on ne devine plus (avant : PUT /users/patient/{id},
  // qui n'enregistrait que nom / email d'un pro) → erreur explicite, l'admin
  // passe par la fiche Patients ou Professionnels.
  users: {
    path: "users",
    clientFilter: DELETED_ACCOUNTS_FILTER,
    updatePath: (id, data) => {
      if (data?.type === "PROFESSIONAL" || data?.type === "PRO") return `users/pro/${encodeURIComponent(id)}`;
      if (data?.type === "PATIENT") return `users/patient/${encodeURIComponent(id)}`;
      const err = new Error("Type de compte inconnu : modifiez-le depuis « Patients » ou « Professionnels ».");
      err.status = 400;
      throw err;
    },
  },
  professionals: {
    path: "users",
    listFilter: { type: "PROFESSIONAL", hideTestUsers: true },
    clientFilter: DELETED_ACCOUNTS_FILTER,
    updatePath: (id) => `users/pro/${encodeURIComponent(id)}`,
  },
  patients: {
    path: "users",
    listFilter: { type: "PATIENT", hideTestUsers: true },
    clientFilter: DELETED_ACCOUNTS_FILTER,
    updatePath: (id) => `users/patient/${encodeURIComponent(id)}`,
  },
  // Comptes incomplets / d'un autre format (invitation jamais finalisée,
  // ligne sans type, ancien format) — invisibles dans les deux listes ci-dessus.
  "incomplete-accounts": { path: "admin/accounts/incomplete" },
  "pro-search": { path: "pro/search" },
  "pro-search-geo": { path: "pro/search/geo" },
  "pro-search-job-speciality": { path: "pro/search-by-job-speciality" },
  "pro-search-slots": { path: "pro/search/available-slots" },
  "pro-featured": { path: "pro/featured" },
  "pro-unvalidated": { path: "pro/unvalidated" },
  "pro-bank-info": { path: "pro" }, // gérée à la main dans la ressource
  "stripe-connect": { path: "pro" }, // gérée à la main
  "stripe-transfers": { path: "pro" }, // gérée à la main

  // Suggestions / taxonomie
  "suggestions-specialities": { path: "admin/suggestions/specialities" },
  // GET /admin/suggestions/jobs : suggestions en attente, non paginé, ni
  // GET /{id} ni PUT /{id} (approve / reject seulement).
  "suggestions-jobs": {
    path: "admin/suggestions/jobs",
    clientSide: { searchFields: ["jobName", "suggestedByName", "suggestedByEmail"] },
    getOneFromList: true,
  },
  // GET /users/specialities renvoie TOUTE la liste (ni pagination, ni
  // recherche, ni GET /{id}) : recherche / tri / pagination côté BO, fiche
  // lue depuis la liste. PUT/DELETE /users/specialities/{id} existent.
  specialities: {
    path: "users/specialities",
    clientSide: { searchFields: ["name", "domainMedical", "job"] },
    getOneFromList: true,
  },
  "traits-hobbies": {
    path: "pro/traits-hobbies",
    // Le backend retourne un DTO unique
    // { traits: string[], hobbies: string[], jobs: string[], about }
    // On l'aplatit ici pour afficher une liste simple lecture seule.
    transformList: (json) => {
      const traits = (json?.traits || []).map((name, i) => ({
        id: `TRAIT::${name}`,
        kind: "TRAIT",
        name,
      }));
      const hobbies = (json?.hobbies || []).map((name, i) => ({
        id: `HOBBY::${name}`,
        kind: "HOBBY",
        name,
      }));
      return [...traits, ...hobbies];
    },
  },
  // GET /pro/jobs → liste de libellés (string) ; pas de GET /{id} (GET
  // /pro/{x} est la fiche d'un pro !) → fiche lue depuis la liste.
  jobs: { path: "pro/jobs", getOneFromList: true },

  // Reports / friendships
  friendships: { path: "patients" }, // path dynamique
  reportedUsers: { path: "reports" },
  "report-users": { path: "users/report/reported" },
  "report-practitioners": { path: "reports/practitioners" },
  "report-echos": { path: "echos/reports/echos" },

  // Agenda
  // GET /bookings : dateFrom / dateTo = epoch ms (Long) sur la date de
  // création ; le DateInput du BO donne "YYYY-MM-DD" → conversion Paris.
  bookings: {
    path: "bookings",
    transformFilter: (f) => ({
      ...f,
      dateFrom: parisDayToEpoch(f.dateFrom, false),
      dateTo: parisDayToEpoch(f.dateTo, true),
    }),
  },
  // GET /bookings/awaiting-review?proId=… (proId obligatoire, liste non
  // paginée de BookingDTO) : RDV terminés que le pro n'a pas encore évalués.
  "awaiting-review": {
    path: "bookings/awaiting-review",
    requiredFilters: ["proId"],
    clientSide: { searchFields: ["firstNamePatient", "lastNamePatient", "patientId", "nameSpeciality"] },
  },
  calendars: { path: "calendars" },
  slots: { path: "calendars" }, // path dynamique
  consultations: { path: "consultations" },
  "consultation-feedbacks": { path: "consultations/feedback" },
  "video-sessions": { path: "consultations" },

  // Contenu / social
  // Lecture : /articles(/{id}). Écriture : routes du pro auteur (cf. ressource).
  articles: {
    path: "articles",
    updatePath: (id, data) =>
      `pro/${encodeURIComponent(data?.professionalId || "")}/articles/${encodeURIComponent(id)}`,
    deletePath: (id, data) =>
      `pro/${encodeURIComponent(data?.professionalId || "")}/articles/${encodeURIComponent(id)}`,
    createPath: (data) => `pro/${encodeURIComponent(data?.professionalId || "")}/articles`,
  },
  // GET /articles/{articleId}/comments — commentaires : PUT/DELETE /articles/comments/{id}
  "article-comments": {
    dynamicPath: "articles/:articleId/comments",
    itemPath: "articles/comments/:id",
  },
  echos: { path: "echos", paginationStyle: "oneIndexed" },
  // GET /echos/{echoId}/comments (page 1-indexée + limit). Pas de lecture
  // unitaire. Pas d'endpoint de liste des likes (ressource echo-likes retirée).
  "echo-comments": {
    dynamicPath: "echos/:echoId/comments",
    paginationStyle: "oneIndexed",
  },
  // GET /echos/themes → EchoThemeDTO { code, label } (pas d'id) : id = code.
  "echo-themes": { path: "echos/themes", getOneFromList: true, getId: (r) => r?.code },
  // GET /events : liste complète non paginée → pagination / recherche côté BO.
  events: { path: "events", clientSide: { searchFields: ["title", "description", "location"] } },
  "event-comments": { dynamicPath: "events/:eventId/comments" },
  videos: { path: "videos" },
  // /pro/{proId}/videos — PUT/DELETE /pro/{proId}/videos/{videoId}
  "pro-videos": {
    dynamicPath: "pro/:proId/videos",
    itemPath: "pro/:proId/videos/:id",
  },
  // includeDrafts=true : liste admin brouillons compris (liste complète →
  // pagination côté BO).
  formations: {
    path: "formations",
    listFilter: { includeDrafts: true },
    clientSide: { searchFields: ["title", "subtitle"] },
  },
  "formation-facets": { path: "formations/facets" },
  // recordings: voir entrée ci-dessous, redirigée vers /admin/recordings
  meetings: { path: "meetings" },
  listings: { path: "listings" },
  "listing-applications": { dynamicPath: "listings/:listingId/applications" },
  "my-applications": { path: "listings/my-applications" },

  // Soin / gamification
  // GET /follow-up/programs?patientId=… (patientId obligatoire côté backend)
  "follow-up-programs": { path: "follow-up/programs", requiredFilters: ["patientId"] },
  // GET /follow-up/programs/{programId}/entries (page 1-indexée + limit)
  "follow-up-entries": {
    dynamicPath: "follow-up/programs/:programId/entries",
    paginationStyle: "oneIndexed",
  },
  // Listes complètes non paginées côté backend (sinon les lignes se répètent
  // de page en page) → pagination / recherche côté BO.
  exercises: { path: "exercises", clientSide: { searchFields: ["title", "name", "category"] } },
  // ExerciseCategoryDTO : { id (= nom de la catégorie), exerciseCount }.
  "exercise-categories": { path: "exercises/categories", getOneFromList: true, clientSide: { searchFields: ["id"] } },
  // /pro/{proId}/exercises — PUT/DELETE /pro/{proId}/exercises/{exerciseId}
  "pro-exercises": {
    dynamicPath: "pro/:proId/exercises",
    itemPath: "pro/:proId/exercises/:id",
  },
  "patient-exercises": { dynamicPath: "patients/:patientId/exercises" },
  // GET /patients/{patientId}/stats/health → objet unique, affiché en 1 ligne.
  // GET /patients/{patientId}/stats/health?from&to : mois « YYYY-MM »
  // obligatoires (YearMonth.parse, 400 sinon) → par défaut le mois précédent
  // et le mois en cours (≈ 30 derniers jours).
  "patient-stats": {
    dynamicPath: "patients/:patientId/stats/health",
    transformFilter: (f) => {
      const month = (d) => d.toISOString().slice(0, 7);
      const to = new Date();
      const from = new Date(to.getFullYear(), to.getMonth() - 1, 1, 12);
      return { ...f, from: f.from || month(from), to: f.to || month(to) };
    },
    transformList: (json, used) =>
      json && typeof json === "object" && !Array.isArray(json)
        ? [{ ...json, id: used?.patientId || "stats", patientId: used?.patientId }]
        : [],
  },
  "patient-ranking": {
    path: "patient-ranking",
    // PatientRankingPageDTO renvoie { entries, totalElements, page, size }
    transformList: (json) => Array.isArray(json?.entries) ? json.entries : [],
  },
  ranking: {
    path: "ranking",
    transformList: (json) => Array.isArray(json?.entries) ? json.entries : [],
  },
  wellness: { path: "wellness" }, // path dynamique
  // GET /questionnaires ne renvoie que ceux de l'appelant (vide pour un
  // admin) : liste par pro via /questionnaires/pro/{proId}, fiche via
  // /questionnaires/{id}. Lecture seule (écritures réservées au pro auteur).
  questionnaires: {
    dynamicPath: "questionnaires/pro/:proId",
    itemPath: "questionnaires/:id",
    clientSide: { searchFields: ["title", "description"] },
  },
  // questionnaire-responses : aucun endpoint de lecture des réponses
  // (POST uniquement) → ressource retirée.
  // Liste : GET /documents/all ; suppression : DELETE /documents/{id}
  documents: { path: "documents/all", itemPath: "documents/:id" },
  factures: { path: "documents" }, // path dynamique
  tracking: { path: "tracking" },

  // Argent
  payments: { path: "payment" },
  // Pas de GET /forfaits/{id} (ni POST /forfaits) : fiche lue depuis la liste.
  forfaits: { path: "forfaits", getOneFromList: true },
  products: { path: "products", clientSide: { searchFields: ["name", "title", "brand", "description"] } },
  "product-reviews": { path: "products" }, // path dynamique
  "product-validations": { path: "products" }, // path dynamique
  pricing: { path: "pricing/breakdown" }, // outil, pas une vraie ressource
  affiliations: { path: "admin/affiliation-codes", getOneFromList: true },
  referrals: { path: "referral/dashboard" }, // singulier côté backend
  "referral-dashboard": { path: "referral/dashboard" },
  // /pro/{proId}/broadcast-lists — PUT/DELETE /pro/{proId}/broadcast-lists/{listId}
  "broadcast-lists": {
    dynamicPath: "pro/:proId/broadcast-lists",
    itemPath: "pro/:proId/broadcast-lists/:id",
  },
  // GET /reviews (paginé) + DELETE /reviews/{id} uniquement.
  reviews: { path: "reviews", getOneFromList: true },

  // Communication
  conversations: { path: "chat/all-conversation" },
  messages: { path: "chat/conversation" },
  // GET /notifications/user/{userId} ; DELETE /notifications/{id}
  notifications: {
    dynamicPath: "notifications/user/:userId",
    itemPath: "notifications/:id",
    clientSide: { searchFields: ["title", "message", "type"] },
  },
  "notifications-user": { path: "notifications" }, // path dynamique
  // GET /contact (liste complète, non paginée) + POST : lecture seule.
  support: {
    path: "contact",
    clientSide: { searchFields: ["title", "description", "userId"] },
    getOneFromList: true,
  },
  contact: {
    path: "contact",
    clientSide: { searchFields: ["title", "description", "userId"] },
    getOneFromList: true,
  },

  // Système
  "dashboard-widgets": { path: "user/dashboard-widget" },
  cache: { path: "cache" },
  "migration-logs": { path: "admin/migration-logs", getOneFromList: true },
  recordings: { path: "admin/recordings", getOneFromList: true },
  geocode: { path: "geocode/autocomplete", absoluteOverride: "/api/geocode/autocomplete" },
  "test-mail": { path: "internal/mail/test" },
  "chatgpt-sessions": { path: "chat/sessions", getOneFromList: true }, // snapshot IA in-memory (GET /chat/sessions)
  ambassadors: { path: "users", listFilter: { type: "AMBASSADOR" } },
  "ura-transactions": { path: "admin/ura-transactions" },
  "affiliation-transactions": { path: "admin/affiliation-transactions" },
  "email-outbox": { path: "admin/email-outbox", getOneFromList: true },
  "discount-bonus": { path: "admin/discount-bonus" },
};

export const resolveResource = (resource) => {
  const cfg = resourceMap[resource];
  if (!cfg) return { path: resource, listFilter: {} };
  return { ...cfg, path: cfg.path, listFilter: cfg.listFilter || {} };
};

const missingParamError = (resource, name) => {
  const err = new Error(
    `« ${resource} » : le filtre « ${name} » est obligatoire (endpoint backend rattaché à un parent).`
  );
  err.status = 400;
  err.code = "MISSING_PARENT";
  return err;
};

// Construit le chemin de LISTE (ou de création) :
//  - `dynamicPath: "articles/:articleId/comments"` → les :params sont pris
//    dans `filter` puis retirés de la query string ;
//  - `requiredFilters` → filtres obligatoires sur un chemin statique.
// Lève une erreur (code MISSING_PARENT) si un paramètre manque : en liste,
// le dataProvider n'appelle alors pas le backend et renvoie une liste vide
// (l'écran affiche la consigne « renseignez le filtre … »).
// Retourne { path, rest, used } : rest = filtres restants (query string),
// used = paramètres consommés par le chemin (recopiés sur chaque ligne par
// le dataProvider, pour que itemPath puisse les retrouver).
export const buildDynamicPath = (resource, cfg, filter = {}) => {
  const rest = { ...(filter || {}) };
  (cfg.requiredFilters || []).forEach((name) => {
    if (rest[name] == null || rest[name] === "") throw missingParamError(resource, name);
  });
  if (!cfg.dynamicPath) return { path: cfg.path, rest, used: {} };
  const used = {};
  const path = cfg.dynamicPath.replace(/:([A-Za-z_]+)/g, (_, name) => {
    const v = rest[name];
    if (v == null || v === "") throw missingParamError(resource, name);
    used[name] = v;
    delete rest[name];
    return encodeURIComponent(v);
  });
  return { path, rest, used };
};

// Chemin d'un enregistrement (getOne / update / delete).
//  - ressource statique : `${path}/${id}` ;
//  - `itemPath: "pro/:proId/videos/:id"` → :params pris dans `data`
//    (previousData / données du formulaire) ;
//  - ressource dynamique sans itemPath : pas d'opération unitaire côté backend.
export const buildItemPath = (resource, cfg, id, data = {}) => {
  if (cfg.itemPath) {
    const values = { ...(data || {}), id };
    return cfg.itemPath.replace(/:([A-Za-z_]+)/g, (_, name) => {
      const v = values[name];
      if (v == null || v === "") throw missingParamError(resource, name);
      return encodeURIComponent(v);
    });
  }
  if (cfg.dynamicPath) {
    const err = new Error(
      `« ${resource} » : aucune opération unitaire n'est exposée par le backend pour cette ressource.`
    );
    err.status = 405;
    throw err;
  }
  return `${cfg.path}/${encodeURIComponent(id)}`;
};
